import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface RoleDistribution {
  role_name: string;
  member_count: number;
  percentage: number;
}

export const useOrganizationRoleDistribution = (providerId: string) => {
  const [roleDistribution, setRoleDistribution] = useState<RoleDistribution[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRoleDistribution = async () => {
      if (!providerId) return;

      try {
        setIsLoading(true);
        setError(null);

        // Get role distribution by joining representatives with their roles
        const { data: roleData, error: roleError } = await supabase
          .from('peo_representatives')
          .select(`
            organizational_role_id,
            peo_organizational_roles:organizational_role_id (
              role_name
            )
          `)
          .eq('provider_id', providerId)
          .eq('verification_status', 'verified');

        if (roleError) throw roleError;

        // Count members by role
        const roleCounts = new Map<string, number>();
        let total = 0;

        (roleData || []).forEach(rep => {
          const roleName = rep.peo_organizational_roles?.role_name || 'Unassigned';
          roleCounts.set(roleName, (roleCounts.get(roleName) || 0) + 1);
          total++;
        });

        // Convert to array with percentages
        const distribution: RoleDistribution[] = Array.from(roleCounts.entries()).map(([role_name, member_count]) => ({
          role_name,
          member_count,
          percentage: total > 0 ? (member_count / total) * 100 : 0
        })).sort((a, b) => b.member_count - a.member_count);

        setRoleDistribution(distribution);
        setTotalMembers(total);
      } catch (err) {
        console.error('Error fetching role distribution:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch role distribution');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleDistribution();
  }, [providerId]);

  return { roleDistribution, totalMembers, isLoading, error };
};